import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { clusterNodes, clusterRadius, markerRadius, radiusMToPx } from '../lib/cluster';
import {
  DEFAULT_VIEW,
  batteryColor,
  coverageColor,
  densityBubbleRadiusM,
  formatAge,
  hardwareName,
  isOnline,
  precisionRadiusM,
  roleName,
  signalColor,
  signalLabel,
  type HexCell,
  type NodeRecord,
  type PositionPoint,
} from '@mcoverage/shared';

export type LayerMode = 'nodes' | 'hex' | 'both' | 'coverage' | 'all';

/**
 * Leaflet-Pane der Node-Marker.
 *
 * Leaflet gibt einen Klick immer an die oberste interaktive Form unter dem Cursor,
 * und eine Ebene, die geleert und neu gezeichnet wird, landet dabei immer wieder
 * ganz oben in der Zeichenreihenfolge. Bei Zellgroessen von einem bis mehreren
 * Kilometern wuerde die Dichteschicht so jeden Klick auf einen Node-Marker
 * schlucken -- die Blasen muessen also unter den Markern liegen, nicht umgekehrt.
 *
 * Ein eigenesPane statt einer blossen Reihenfolge beim addTo, weil das Umhängen
 * nichts bringt: clearLayers() plus Neuzeichnen schiebt die Formen jedes Mal
 * wieder nach hinten. Das Pane haelt die Marker dagegen dauerhaft ueber den
 * Hexebenen -- und ueber der Kacheln, unter den Tooltips (650) und Popups (700).
 */
const NODE_PANE = 'nodePane';
const NODE_PANE_Z = '450';

/**
 * Zellen, die ihre Zahl dauerhaft zeigen.
 *
 * Eine eigene Pane ist keine Option, wenn der Tooltip beim Ueberfahren kommen
 * soll: Leaflet liefert Mausereignisse nur an das oberste Canvas, und die
 * Hexebenen liegen nun unter den Markern. Die Zahl wandert deshalb in die
 * Tooltip-Pane -- als dauerhaftes, nicht klickbares Label. Begrenzt auf die
 * dichtesten Zellen und auf Blasen, in die eine Zahl ueberhaupt passt.
 */
const CELL_LABEL_LIMIT = 120;
const CELL_LABEL_MIN_PX = 12;

export interface MapViewProps {
  nodes: NodeRecord[];
  hexes: HexCell[];
  /** Resolution the density cells were aggregated at; sizes the bubbles. */
  hexResolution: number;

  /** Measured coverage cells; empty until somebody maps. */
  coverage?: HexCell[];
  track: PositionPoint[] | null;
  playhead: number | null;
  layerMode: LayerMode;
  showPrecision: boolean;
  selected: NodeRecord | null;
  onSelect: (node: NodeRecord | null) => void;
  onBoundsChange: (bounds: string) => void;
  /** Zoom level of the current viewport; drives the hex resolution. */
  onZoomChange: (zoom: number) => void;
  loading: boolean;
  truncated: boolean;
  total: number;
}

/**
 * IDs der Zellen, die ihre Zahl dauerhaft in der Blase zeigen.
 *
 * Die Zahl steht nur in den dichtesten Zellen und nur, wenn die Blase auf dem
 * Bildschirm gross genug ist, um sie zu tragen. Beides ist eine Bremse: ein
 * dauerhaftes Label ist ein DOM-Knoten, und eines pro Zelle ergaebe bei feiner
 * Aufloesung tausende davon.
 */
function labelledCellIds(cells: HexCell[], radiusM: number, map: L.Map): Set<string> {
  const zoom = map.getZoom();
  const ids = new Set<string>();
  for (const cell of [...cells].sort((a, b) => b.nodes - a.nodes)) {
    if (ids.size >= CELL_LABEL_LIMIT) break;
    if (radiusMToPx(radiusM, cell.center[0], zoom) < CELL_LABEL_MIN_PX) continue;
    ids.add(cell.id);
  }
  return ids;
}

function popupHtml(node: NodeRecord): string {
  const esc = (s: string) =>
    s.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
    );

  const now = Date.now() / 1000;
  const rows: string[] = [];

  // Nodes only reveal their name once the mesh broadcasts their node info,
  // which can take hours. Until then the ID has to carry the popup.
  const named = node.longName !== '' || node.shortName !== '';
  if (named) {
    rows.push(`<div class="pop-name">${esc(node.longName || node.shortName)}</div>`);
    if (node.longName && node.shortName) {
      rows.push(`<div class="pop-short">${esc(node.shortName)}</div>`);
    }
  } else {
    rows.push(`<div class="pop-name unnamed">${esc(node.userId)}</div>`);
    rows.push('<div class="pop-unnamed-note">Name noch unbekannt — der Node hat bisher keine NodeInfo gesendet</div>');
  }

  rows.push(
    `<div class="pop-role">${esc(roleName(node.role) || 'Client')} · ${esc(hardwareName(node.hardware))}</div>`,
  );
  if (named) rows.push(`<div class="pop-id">${esc(node.userId)}</div>`);

  const bits: string[] = [];
  if (node.batteryLevel !== null) bits.push(`🔋 ${node.batteryLevel}%`);
  if (node.voltage !== null) bits.push(`${node.voltage.toFixed(2)} V`);
  if (node.snr !== null) bits.push(`SNR ${node.snr.toFixed(1)}`);
  if (node.rssi !== null) bits.push(`${node.rssi} dBm (${signalLabel(node.rssi)})`);
  if (bits.length) rows.push(`<div class="pop-row">${bits.join(' · ')}</div>`);

  rows.push(`<div class="pop-row">Gehört: ${formatAge(now - node.lastHeard)}</div>`);

  if (node.positionPrecision) {
    const radius = precisionRadiusM(node.positionPrecision);
    if (radius) {
      rows.push(
        `<div class="pop-note">Positionsgenauigkeit ±${radius >= 1000 ? `${(radius / 1000).toFixed(1)} km` : `${radius} m`} (${node.positionPrecision} Bit)</div>`,
      );
    }
  }

  rows.push('<div class="pop-cta">Klicke für Details & Track</div>');
  return `<div class="pop">${rows.join('')}</div>`;
}

export function MapView({
  nodes,
  hexes,
  hexResolution,
  coverage = [],
  track,
  playhead,
  layerMode,
  showPrecision,
  selected,
  onSelect,
  onBoundsChange,
  onZoomChange,
  loading,
  truncated,
  total,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const nodeLayerRef = useRef<L.LayerGroup | null>(null);
  // Dichte und Coverage brauchen eigene Gruppen: sie teilen sich dieselbe
  // Aufloesung, wuerden sich in einer Gruppe also gegenseitig loeschen statt
  // sich zu ergaenzen.
  const densityLayerRef = useRef<L.LayerGroup | null>(null);
  const coverageLayerRef = useRef<L.LayerGroup | null>(null);
  const precisionLayerRef = useRef<L.LayerGroup | null>(null);
  const trackLayerRef = useRef<L.LayerGroup | null>(null);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const boundsRef = useRef(onBoundsChange);
  boundsRef.current = onBoundsChange;
  const zoomRef = useRef(onZoomChange);
  zoomRef.current = onZoomChange;

  // --- map init ------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: DEFAULT_VIEW.center,
      zoom: DEFAULT_VIEW.zoom,
      zoomControl: false,
      preferCanvas: true,
      worldCopyJump: true,
      attributionControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      crossOrigin: true,
    }).addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);
    // Attribution moves to the bottom-right stack because the bottom-left
    // corner belongs to the scale bar and the legend card.
    L.control.attribution({ position: 'bottomright', prefix: false })
      .addAttribution('© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>')
      .addTo(map);
    L.control.scale({ position: 'bottomleft', metric: true, imperial: false, maxWidth: 110 })
      .addTo(map);

    // Must exist before the first marker is drawn.
    map.createPane(NODE_PANE).style.zIndex = NODE_PANE_Z;

    nodeLayerRef.current = L.layerGroup().addTo(map);
    densityLayerRef.current = L.layerGroup();
    coverageLayerRef.current = L.layerGroup();
    precisionLayerRef.current = L.layerGroup();
    trackLayerRef.current = L.layerGroup().addTo(map);

    // Bounds and zoom are reported together: both decide what the API is asked
    // for, so splitting them would only allow one half of the viewport to be
    // out of date.
    const reportViewport = () => {
      const b = map.getBounds();
      boundsRef.current(
        [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()]
          .map((v) => v.toFixed(4))
          .join(','),
      );
      zoomRef.current(map.getZoom());
    };

    map.on('moveend zoomend', reportViewport);

    // Report the initial viewport so the first fetch is already scoped.
    reportViewport();

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      nodeLayerRef.current = null;
      densityLayerRef.current = null;
      coverageLayerRef.current = null;
      precisionLayerRef.current = null;
      trackLayerRef.current = null;
    };
  }, []);

  // --- track playback ------------------------------------------------------
  useEffect(() => {
    const layer = trackLayerRef.current;
    if (!layer) return;
    layer.clearLayers();

    if (!track || track.length === 0) return;

    const all = track.map((p) => [p.lat, p.lon] as [number, number]);

    // Full route, dimmed.
    if (all.length > 1) {
      L.polyline(all, {
        color: '#64748b',
        weight: 2,
        opacity: 0.7,
        dashArray: '4 6',
      }).addTo(layer);
    }

    const head = playhead === null ? all.length - 1 : playhead;
    const travelled = all.slice(0, Math.max(1, head + 1));
    if (travelled.length > 1) {
      L.polyline(travelled, { color: '#22d3ee', weight: 3, opacity: 0.95 }).addTo(layer);
    }

    const start = all[0]!;
    L.circleMarker(start, {
      radius: 5,
      color: '#0f172a',
      weight: 2,
      fillColor: '#22c55e',
      fillOpacity: 1,
    })
      .bindTooltip('Start', { permanent: false })
      .addTo(layer);

    const current = all[head] ?? start;
    const point = track[head];
    if (point) {
      L.circleMarker(current, {
        radius: 7,
        color: '#0f172a',
        weight: 2,
        fillColor: '#22d3ee',
        fillOpacity: 1,
      })
        .bindTooltip(
          `${new Date(point.time * 1000).toLocaleString('de-DE')}${
            point.snr !== null ? ` · SNR ${point.snr.toFixed(1)}` : ''
          }`,
          { permanent: false },
        )
        .addTo(layer);
    }

    // Frame the whole route the first time a track arrives.
    const map = mapRef.current;
    if (map && playhead === null && all.length > 1) {
      map.fitBounds(L.latLngBounds(all).pad(0.2));
    }
  }, [track, playhead]);

  // --- density bubbles and coverage hexes ---------------------------------
  const showDensity = layerMode === 'hex' || layerMode === 'both' || layerMode === 'all';
  const showCoverage = layerMode === 'coverage' || layerMode === 'all';

  useEffect(() => {
    const layer = densityLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;

    layer.clearLayers();
    if (!showDensity) return;
    if (!map.hasLayer(layer)) layer.addTo(map);

    const maxNodes = hexes.reduce((m, c) => Math.max(m, c.nodes), 1);
    // Blasen statt Hexagons: der Server aggregiert weiter in H3-Zellen, nur die
    // Darstellung rundet ab. Der Radius folgt der Zellflaeche, damit eine Blase
    // genauso viel Platz einnimmt wie das Hexagon, das sie ersetzt, und die
    // Dichte allein in der Fuellung steckt.
    const radius = densityBubbleRadiusM(hexResolution);
    const labelled = labelledCellIds(hexes, radius, map);
    for (const cell of hexes) {
      const intensity = Math.min(1, cell.nodes / Math.max(3, maxNodes * 0.6));
      const bubble = L.circle(cell.center, {
        radius,
        color: '#38bdf8',
        weight: 1,
        opacity: 0.45,
        fillColor: '#0ea5e9',
        fillOpacity: 0.06 + intensity * 0.32,
      });
      if (labelled.has(cell.id)) {
        bubble.bindTooltip(cell.nodes.toLocaleString('de-DE'), {
          permanent: true,
          direction: 'center',
          className: 'cell-count',
        });
      }
      bubble.addTo(layer);
    }
  }, [hexes, hexResolution, showDensity]);

  useEffect(() => {
    const layer = coverageLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;

    layer.clearLayers();
    if (!showCoverage) return;
    if (!map.hasLayer(layer)) layer.addTo(map);

    const labelled = labelledCellIds(
      coverage.map((c) => ({ ...c, nodes: c.samples ?? 0 })),
      densityBubbleRadiusM(hexResolution),
      map,
    );
    for (const cell of coverage) {
      const snr = cell.bestSnr;
      const hex = L.polygon(cell.polygon as L.LatLngExpression[], {
        color: coverageColor(snr),
        weight: 1,
        opacity: 0.85,
        fillColor: coverageColor(snr),
        // More measurements => more trustworthy => more opaque.
        fillOpacity: Math.min(0.75, 0.2 + Math.log2((cell.samples ?? 1) + 1) * 0.12),
      });
      if (labelled.has(cell.id)) {
        hex.bindTooltip(snr !== null ? `${snr.toFixed(1)} dB` : '?', {
          permanent: true,
          direction: 'center',
          className: 'cell-count',
        });
      }
      hex.addTo(layer);
    }
  }, [coverage, hexResolution, showCoverage]);

  // --- node markers --------------------------------------------------------
  const zoom = mapRef.current?.getZoom() ?? DEFAULT_VIEW.zoom;

  useEffect(() => {
    const layer = nodeLayerRef.current;
    if (!layer) return;
    layer.clearLayers();

    if (layerMode === 'hex') return;

    const now = Date.now() / 1000;
    const cells = clusterNodes(nodes, zoom);

    for (const cell of cells) {
      if (cell.nodes.length === 1) {
        const node = cell.nodes[0]!;
        const online = isOnline(node.lastHeard, now);
        const color = node.lat === null ? '#64748b' : signalColor(node.rssi);

        const marker = L.circleMarker([node.lat ?? 0, node.lon ?? 0], {
          pane: NODE_PANE,
          radius: markerRadius(node.role, zoom),
          color: node.role === 5 ? '#a78bfa' : '#0f172a',
          weight: node.role === 5 ? 2 : 1,
          fillColor: color,
          fillOpacity: online ? 0.95 : 0.35,
        });

        marker.bindPopup(popupHtml(node), { className: 'pop-wrap', maxWidth: 280 });
        marker.on('click', () => selectRef.current(node));
        marker.addTo(layer);
      } else {
        const onlineCount = cell.nodes.filter((n) => isOnline(n.lastHeard, now)).length;
        const hasRssi = cell.nodes.some((n) => n.rssi !== null);
        const radius = clusterRadius(cell.nodes.length);
        // White halo instead of a dark stroke: overlapping bubbles then blend
        // into one another instead of accumulating into a black smear.
        const marker = L.circleMarker([cell.lat, cell.lon], {
          pane: NODE_PANE,
          radius,
          color: '#ffffff',
          weight: 2,
          fillColor: hasRssi ? '#0ea5e9' : '#64748b',
          fillOpacity: onlineCount > 0 ? 0.82 : 0.5,
        });
        // The count sits inside the bubble, but only where it actually fits --
        // one label per bubble would turn a dense map into overlapping digits.
        if (radius >= 12) {
          marker.bindTooltip(cell.nodes.length.toLocaleString('de-DE'), {
            permanent: true,
            direction: 'center',
            className: 'cluster-count',
          });
        }
        marker.on('click', () => {
          const bounds = L.latLngBounds([]);
          for (const n of cell.nodes) {
            if (n.lat !== null && n.lon !== null) bounds.extend([n.lat, n.lon]);
          }
          if (bounds.isValid()) mapRef.current?.fitBounds(bounds.pad(0.15));
        });
        marker.addTo(layer);
      }
    }
  }, [nodes, layerMode, zoom]);

  // --- position precision circles -----------------------------------------
  useEffect(() => {
    const layer = precisionLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;

    layer.clearLayers();
    if (!showPrecision || layerMode === 'hex') return;
    if (!map.hasLayer(layer)) layer.addTo(map);

    // Only draw these where they are actually resolvable on screen.
    if ((mapRef.current?.getZoom() ?? 0) < 9) return;

    for (const node of nodes) {
      if (node.lat === null || node.lon === null) continue;
      const radius = precisionRadiusM(node.positionPrecision);
      if (!radius) continue;
      L.circle([node.lat, node.lon], {
        radius,
        // Purely informative: no tooltip, no click handler. Without this the
        // circles would take the click away from the node marker underneath.
        interactive: false,
        color: '#38bdf8',
        weight: 0.8,
        opacity: 0.55,
        fillColor: '#38bdf8',
        fillOpacity: 0.06,
      }).addTo(layer);
    }
  }, [nodes, showPrecision, layerMode]);

  // --- selected node -------------------------------------------------------
  useEffect(() => {
    if (!selected || selected.lat === null || selected.lon === null) return;
    const map = mapRef.current;
    if (!map) return;
    // One setView instead of panTo + setZoom: a second setView reads the
    // center that the still-running pan has not reached yet and cancels the pan.
    map.setView([selected.lat, selected.lon], Math.max(map.getZoom(), 13), { animate: true });
  }, [selected]);

  const capacityNote = useMemo(() => {
    if (!truncated) return null;
    return `${nodes.length.toLocaleString('de-DE')} von ${total.toLocaleString('de-DE')} Nodes im Ausschnitt — zoome hinein für mehr.`;
  }, [truncated, nodes.length, total]);

  return (
    <div className="map-wrap">
      <div ref={containerRef} className="map" role="application" aria-label="Meshtastic-Nodes auf der Karte" />
      {loading && (
        <div className="map-loading">
          <span className="spinner" aria-hidden />
          Lade Nodes…
        </div>
      )}
      {capacityNote && <div className="map-note">{capacityNote}</div>}
    </div>
  );
}

export { batteryColor };